import { useMemo, useRef, useState } from 'react';
import { useLoaderData, useLocation } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';
import { ItemShippingProvider } from '@modules/ecommerce/context/ItemShippingContext';
import { VariantShippingMethodsProvider } from '~/modules/ecommerce/context/VariantShippingMethodsContext';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useMeasuredPriceUnit } from '@modules/product/hook/use-measured-price-unit';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import { useVariantStock } from '@modules/product/hook/use-variant-stock';
import { useAddToCartWithTracking } from '@modules/product/hook/use-add-to-cart-with-tracking';
import SpecialtyPricingBanner from '@modules/product/component/specialty-pricing-banner/SpecialtyPricingBanner';
import TradeOnlyBanner from '@modules/product/component/trade-only-banner/TradeOnlyBanner';
import ProductLocationsDrawer from '~/modules/product/component/product-details/ProductLocationsDrawer';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    MOBILE_DETAIL_BYLINE,
    MOBILE_DETAIL_TITLE,
    TrackedWebsiteCta,
    detailCta,
    useIslandFallback,
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { MobileVariantRows } from './MobileVariantRows';
import { PurchaseDrawer } from './PurchaseDrawer';
import { MobileProductDescription } from './MobileProductDescription';
import { MobileCartConfirmSheet } from './MobileCartConfirmSheet';
import { ProductShopBanner } from './ProductShopBanner';
import { useTrackProductProfileClick } from '../../hooks/use-track-product-profile-click';
import {
    PurchasePriceBlock,
    useSizesInUse,
    useVariantEnquiryContext,
} from './purchase-flow-parts';

/** Stable hidden-mode claim — blanks the island while the purchase block
 *  crosses the reading line (its own price + CTA are already on screen). */
const SUPPRESS_ISLAND: IslandMode = { kind: 'hidden' };

interface IslandPurchaseFallbackProps {
    priceString: string | null;
    isBuyable: boolean;
    /** The professional accepts enquiries — the island prefers Enquire. */
    showEnquire: boolean;
    /** Enquiry CTA label (professional's configured label or a fallback). */
    enquiryLabel: string;
    onEnquire: () => void;
    /** Fires after a successful island add — opens the mobile confirm sheet. */
    onAdded: () => void;
}

/**
 * Null-rendering owner of the island's persistent purchase pill (price +
 * primary CTA), shown whenever no scrolled section claims the island. Lives
 * inside the shipping providers so the add-to-cart closure carries the real
 * stock/shipping state wherever the island renders.
 *
 * The island prefers the highest-value action: Enquire whenever the
 * professional accepts enquiries (most of the catalogue), even on a buyable
 * product — cart is the fallback only when enquiries are off. Sample-only
 * products with enquiries off set no fallback (their sample flow needs the
 * inline block).
 *
 * The pill carries one action, so a configured Book a meeting button stays in
 * the purchase block and its variant sheet rather than on the island.
 */
const IslandPurchaseFallback = ({
    priceString,
    isBuyable,
    showEnquire,
    enquiryLabel,
    onEnquire,
    onAdded,
}: IslandPurchaseFallbackProps) => {
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { canAddToCart } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });
    const onAddedRef = useRef(onAdded);
    onAddedRef.current = onAdded;
    const { addSelectedToCart, isLoading } = useAddToCartWithTracking({
        // Mobile confirms in its own sheet, not the top-nav cart preview.
        openCartPopup: false,
        onItemAdded: () => onAddedRef.current(),
    });

    // The actions are recreated every render — route them through refs so the
    // memoised mode's identity only tracks meaningful state. An unstable mode
    // would re-run setFallback → context change → re-render, in a loop.
    const addRef = useRef(addSelectedToCart);
    addRef.current = addSelectedToCart;
    const enquireRef = useRef(onEnquire);
    enquireRef.current = onEnquire;

    const mode = useMemo<IslandMode | null>(() => {
        if (showEnquire) {
            return {
                kind: 'purchase',
                price: priceString,
                label: enquiryLabel,
                onClick: () => enquireRef.current(),
            };
        }
        if (isBuyable) {
            return {
                kind: 'purchase',
                price: priceString,
                label: 'Add to Cart',
                onClick: () => addRef.current(),
                disabled: !canAddToCart || isLoading,
            };
        }
        return null;
    }, [
        showEnquire,
        isBuyable,
        priceString,
        enquiryLabel,
        canAddToCart,
        isLoading,
    ]);
    useIslandFallback(mode);

    return null;
};

export interface MobilePurchaseFlowProps {
    priceString: string | null;
    isPriceRange: boolean;
    /** True when the current price is discounted — shows struck-out retail price. */
    isOnSale: boolean;
    /** Retail (original) price formatted for display, or null when not on sale. */
    retailPriceString: string | null;
    /** Price-range lead-in ("From" / "Up to"), matching legacy VariantPricing. */
    pricePrefix: string | null;
    isBuyable: boolean;
    isSampleBuyable: boolean;
    /** Why the selected variant cannot go in the cart ("Out of stock"), or
     *  null when it can. */
    stockNotice: string | null;
    /** Enquiry CTA label (professional's configured label, e.g. "Request Price
     *  / Brochure", or a fallback). Shown on every product, buyable or not. */
    enquiryLabel: string;
    /**
     * The bare enquiry label ("Get in Touch"), reported as the enquiry's
     * `CTA_Label`. The supplier suffix on {@link enquiryLabel} is copy, and
     * would split one button into a label per supplier in the warehouse.
     */
    trackedEnquiryLabel: string;
    /** Whether the professional accepts enquiries (audience gating). Gates the
     *  Enquire CTA — which is now the primary action on every product. */
    showCTA: boolean;
    showSpecifierLoginPrompt: boolean;
    /** A2: show "Request a Sample" enquiry button. */
    showRequestSampleEnquiry: boolean;
    onRequestSampleEnquiry: () => void;
}

/**
 * BC-4524 / BC-4737 — the inline mobile purchase block. Leads with identity:
 * brand eyebrow, the product title (the page's h1 — mobile has no text hero)
 * and the price, so "what is it / how much" sits in the first viewport under
 * the hero. Configuration collapses into summary rows that open the variant
 * bottom sheet ({@link PurchaseDrawer}); the marketing description lives
 * in the (01) Product Details accordion row, not here. The whole transactional
 * path runs inline: add-to-cart with quantity for buyable products, the legacy
 * sample flow for sample-only products, and the enquiry modal for enquiry-led
 * products.
 */
export const MobilePurchaseFlow = ({
    priceString,
    isPriceRange,
    isOnSale,
    retailPriceString,
    pricePrefix,
    isBuyable,
    isSampleBuyable,
    stockNotice,
    enquiryLabel,
    trackedEnquiryLabel,
    showCTA,
    showSpecifierLoginPrompt,
    showRequestSampleEnquiry,
    onRequestSampleEnquiry,
}: MobilePurchaseFlowProps) => {
    const { product, whereToBuyEnabled } = useLoaderData<typeof loader>();
    const trackProfileClick = useTrackProductProfileClick();
    // The sheet resolves a relative share against the origin, so an empty
    // string shared the site root rather than this page. The item's own page
    // is the page we are on.
    const { pathname } = useLocation();
    const { selectedVariant, quantity } = useVariantSelectionState();
    const { quantityNote } = useMeasuredPriceUnit();
    const enquire = useEnquireCta({ source: 'ProductMobileCTA' });
    const bookMeeting = useBookCta({ source: 'ProductMobileCTA' });
    const variantEnquiry = useVariantEnquiryContext();
    const [whereToBuyOpen, setWhereToBuyOpen] = useState(false);
    // Added-to-cart confirmation sheet, shared by every mobile add path
    // (variant sheet, island pill).
    const [cartConfirmOpen, setCartConfirmOpen] = useState(false);
    // One shared variant sheet, opened either from a variant summary row or the
    // "Available to shop" banner — same sheet, same footer order both ways.
    const [sheetOpen, setSheetOpen] = useState(false);
    const openSheet = () => setSheetOpen(true);
    const tracker = useTracker();
    // The banner's opening of the sheet is the shop entry's own event; a
    // variant row opening the same sheet is not.
    const openShopSheet = () => {
        openSheet();
        void tracker.log('ShopBannerOpen', {
            url: new URL(window.location.href),
            data: { LibraryItemID: product.ID },
        });
    };

    const sizesInUse = useSizesInUse();
    // Whether there is anything to pick. With nothing, the description takes
    // the variants' place above the actions; with something, the picks come
    // first and the description follows the actions.
    const { attributeMap } = useVariantOptionState();
    const hasVariants = attributeMap.size > 0 || sizesInUse.length > 0;
    const description = (
        <MobileProductDescription
            key={product.ID}
            topContent={product.ContentTop}
        />
    );
    const isEnquiryLed = !isBuyable && !isSampleBuyable;
    // Enquire is the primary action on every product the professional accepts
    // enquiries for — buyable or not. Commerce moves into the shop banner.
    const showEnquire = showCTA;

    // Brand eyebrow falls back from the professional's name to the brand,
    // matching the desktop text hero; linked when it is the professional.
    const professional = product.Professional;
    const brand = professional?.Title ?? product.Brand ?? '';

    // While this block crosses the island's reading line, blank the island —
    // its own price + CTA are on screen, so the persistent pill would repeat
    // them right below (Apple hides its buy bar the same way).
    const suppressRef = useIslandSection<HTMLElement>(SUPPRESS_ISLAND);

    const onEnquire = () =>
        enquire.open({
            title: enquiryLabel,
            ctaLabel: trackedEnquiryLabel,
            messageSubject: variantEnquiry.messageSubject,
            itemImage: variantEnquiry.itemImage,
            brandImage: variantEnquiry.brandImage,
        });

    const website = product.Website || product.Professional.Website;
    // ••• in the CTAs' white pill, as the profile sets it: the tile sheet —
    // Share, Save, Save to Pinterest — over the product.
    const actionsPanel = (
        <TileActionPanel
            pinId={product.ID}
            pinType="ProductPin"
            bareTrigger
            sheetLayout="page"
            triggerClassName={cn(
                detailCta('white-lifted'),
                'm-0 h-[3.25rem] w-[3.25rem] shrink-0 p-0',
            )}
            share={{ url: pathname, title: product.Title }}
            tileInfo={{
                title: product.Title,
                subtitle: product.Professional.Title,
                image:
                    product.LibraryImages?.[0]?.LibraryImageFile
                        ?.DetailPageNatural?.Desktop ?? undefined,
                imageShape: 'square',
            }}
        />
    );

    return (
        <VariantShippingMethodsProvider
            selectedVariant={selectedVariant}
            quantity={quantity}
            estimatedSampleWeight={product.Professional.SampleEstimatedWeight}
        >
            <ItemShippingProvider>
                <IslandPurchaseFallback
                    priceString={priceString}
                    isBuyable={isBuyable}
                    showEnquire={showEnquire}
                    enquiryLabel={enquiryLabel}
                    onEnquire={onEnquire}
                    onAdded={() => setCartConfirmOpen(true)}
                />
                <section
                    ref={suppressRef}
                    // px-6, the phone page's one gutter: every band below
                    // this one is set 24px in, and the purchase block's 16px
                    // read as a broken indent rather than a tighter measure.
                    className="flex flex-col gap-7 bg-ground px-6"
                >
                    <div>
                        {/* The product, then who makes it: the name is what
                            the reader came for, the supplier qualifies it. */}
                        <h1
                            className={cn(
                                MOBILE_DETAIL_TITLE,
                                'text-[#181818]',
                            )}
                        >
                            {product.Title}
                        </h1>
                        {brand ? (
                            <p
                                className={cn(
                                    MOBILE_DETAIL_BYLINE,
                                    'text-[#767676]',
                                )}
                            >
                                {professional?.Title ? (
                                    <RoccoLink
                                        to={professional.Link}
                                        onClick={trackProfileClick}
                                    >
                                        {brand}
                                    </RoccoLink>
                                ) : (
                                    brand
                                )}
                            </p>
                        ) : null}
                        <div className="mt-4">
                            <PurchasePriceBlock
                                align="start"
                                priceString={priceString}
                                isPriceRange={isPriceRange}
                                isInServiceableArea={
                                    product.IsInServiceableArea
                                }
                                isOnSale={isOnSale}
                                retailPriceString={retailPriceString}
                                pricePrefix={pricePrefix}
                                quantityNote={quantityNote}
                            />
                            {stockNotice ? (
                                <p className="m-0 mt-1 font-sans text-[0.8125rem] leading-[1.2] text-[#767676]">
                                    {stockNotice}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <div className="[&:empty]:hidden">
                        <SpecialtyPricingBanner product={product} />
                        <TradeOnlyBanner selectedVariant={selectedVariant} />
                    </div>

                    {/* Specify first — the variant summary rows open the shared
                        sheet to configure. What you pick attaches to the enquiry
                        below, so a specified lead is the strongest on the page.
                        Self-hides when there is nothing to configure. */}
                    <MobileVariantRows
                        variants={product.Variants}
                        sizes={sizesInUse}
                        onSpecify={openSheet}
                    />
                    {/* The desktop's purchase drawer, as a sheet: the same
                        selector, shipping facts and footer, without the
                        picture. */}
                    <PurchaseDrawer
                        presentation="mobile"
                        open={sheetOpen}
                        onOpenChange={setSheetOpen}
                        isBuyable={isBuyable}
                        isSampleBuyable={isSampleBuyable}
                        priceString={priceString}
                        isPriceRange={isPriceRange}
                        isOnSale={isOnSale}
                        retailPriceString={retailPriceString}
                        pricePrefix={pricePrefix}
                        enquiryLabel={enquiryLabel}
                        trackedEnquiryLabel={trackedEnquiryLabel}
                        showCTA={showCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                    />

                    {hasVariants ? null : description}

                    {/* Actions — one clear hierarchy. Enquire is the primary
                        (QDD) action on every product the professional takes
                        enquiries for; Visit Website is the graceful exit that
                        still earns a referral. Commerce is not here — it lives
                        in the shop banner straight after.

                        The desktop cluster's own buttons: white, lifted off
                        the page's ground, at the L step. Dark is kept for a
                        purchase, and there is none in this stack, so Enquire
                        takes the same white surface desktop gives an
                        enquiry-led primary and leads by being first. */}
                    <div className="flex flex-col gap-3">
                        {/* With no website, the ••• joins the enquiry's row
                            rather than standing alone on one of its own. */}
                        <div className="flex items-stretch gap-2 [&:empty]:hidden">
                            {showEnquire ? (
                                <button
                                    type="button"
                                    onClick={onEnquire}
                                    className={detailCta(
                                        'white-lifted',
                                        'w-full flex-1',
                                    )}
                                >
                                    {enquiryLabel}
                                </button>
                            ) : null}
                            {website ? null : actionsPanel}
                        </div>
                        {/* A configured Book a meeting button, after the
                            enquiry and in the same white pill. */}
                        {bookMeeting.inline ? (
                            <button
                                type="button"
                                onClick={bookMeeting.open}
                                className={detailCta('white-lifted', 'w-full')}
                            >
                                {bookMeeting.label}
                            </button>
                        ) : null}

                        {website ? (
                            <div className="flex items-stretch gap-2">
                                {/* The legacy phone page's only Visit Website
                                    was the company profile block, and the
                                    mobile referral dashboards read its source. */}
                                <TrackedWebsiteCta
                                    website={website}
                                    libraryItemID={product.ID}
                                    professionalID={product.Professional.ID}
                                    professionalTitle={
                                        product.Professional.Title
                                    }
                                    variant="white-lifted"
                                    source="CompanyProfile"
                                    className="flex-1"
                                />
                                {actionsPanel}
                            </div>
                        ) : null}

                        {/* "Request a Sample" is an enquiry (a lead), not a
                            purchase — it stays with the QDD actions. */}
                        {showRequestSampleEnquiry ? (
                            <button
                                type="button"
                                onClick={onRequestSampleEnquiry}
                                className={detailCta('white-lifted', 'w-full')}
                            >
                                Request a Sample
                            </button>
                        ) : null}
                        {whereToBuyEnabled ? (
                            <button
                                type="button"
                                onClick={() => setWhereToBuyOpen(true)}
                                className={detailCta('white-lifted', 'w-full')}
                            >
                                Where to Buy
                            </button>
                        ) : null}
                        {isEnquiryLed &&
                        !showCTA &&
                        showSpecifierLoginPrompt ? (
                            <SpecifierLoginPrompt
                                professionalName={product.Professional.Title}
                                showBottomBorder={false}
                            />
                        ) : null}
                    </div>

                    {/* For the minority of products that can be bought or
                        sampled on ArchiPro, the opt-in shop entry, straight
                        under the actions it stands in for, so commerce reads
                        as part of the same group rather than an afterthought
                        below the description. Guarded so an empty group never
                        renders a stray hairline. With the description
                        following, the banner closes with its own rule; with
                        the accordion following, that opens with one. */}
                    {isBuyable || isSampleBuyable ? (
                        <div
                            className={cn(
                                'flex flex-col divide-y divide-black/10 border-t border-black/10',
                                hasVariants && 'border-b',
                            )}
                        >
                            <ProductShopBanner
                                isBuyable={isBuyable}
                                isSampleBuyable={isSampleBuyable}
                                stockNotice={stockNotice}
                                onOpen={openShopSheet}
                            />
                        </div>
                    ) : null}

                    {hasVariants ? description : null}
                </section>

                {whereToBuyEnabled ? (
                    <ProductLocationsDrawer
                        product={product}
                        open={whereToBuyOpen}
                        toggleModal={setWhereToBuyOpen}
                    />
                ) : null}

                <MobileCartConfirmSheet
                    open={cartConfirmOpen}
                    onOpenChange={setCartConfirmOpen}
                />
            </ItemShippingProvider>
        </VariantShippingMethodsProvider>
    );
};
